import * as React from 'react';
import { Spinner, SpinnerSize, Text, Icon } from '@fluentui/react';
import { Context as ReactContext } from 'react';
import type { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { Context } from '@micro-frontend-react/core/lib/Context';

import { mapApiStatusToUiStatus, type IPBTCaseAudit } from '../../../Interfaces/IPBTCase';
import { getCaseAuditApi } from '../../../Services/PBTCaseService';
import { auditLogStyles } from './AuditLog.styled';

interface AuditLogProps {
  caseId: number;
}

/** Audit fields whose values are persisted as raw workflow status enum names. */
const STATUS_AUDIT_FIELDS = new Set(['Status', 'Attestation']);

/** Audit fields that are date-only business dates persisted as an ISO round-trip timestamp. */
const DATE_ONLY_AUDIT_FIELDS = new Set(['TermConversationDate', 'EffectiveTerminationDate']);

export const formatDateTime = (isoString: string): string => {
  const hasTimeZone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(isoString);
  const date = new Date(hasTimeZone ? isoString : `${isoString}Z`);
  if (Number.isNaN(date.getTime())) {
    return isoString;
  }
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Formats a single audit old/new value for display. Fixes two inconsistencies surfaced in end-to-end
 * testing: (1) Status values were shown as the raw workflow enum name (e.g. "AwaitingAttestation")
 * instead of the current user-facing label ("Date & Attestation Pending"); (2) date-only fields were
 * shown with a misleading midnight timestamp (e.g. "2026-08-19T00:00:00.0000000") that could be
 * mistaken for the recorded action time.
 */
export const formatAuditValue = (fieldName: string, value: string | null): string => {
  if (value === null || value === '') {
    return '(empty)';
  }

  // Render the current status label so the audit history matches the terminology shown everywhere
  // else in the workflow. mapApiStatusToUiStatus is idempotent for already-mapped values.
  if (STATUS_AUDIT_FIELDS.has(fieldName)) {
    return mapApiStatusToUiStatus(value);
  }

  // Show only the calendar date for date-only fields. Parse the Y-M-D parts directly (not via
  // new Date(isoString)) so no timezone conversion can shift the displayed day.
  if (DATE_ONLY_AUDIT_FIELDS.has(fieldName)) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (match) {
      const [, year, month, day] = match;
      const y = Number(year);
      const m = Number(month);
      const d = Number(day);
      const date = new Date(y, m - 1, d);
      // Reject invalid dates (e.g. 2026-02-31): the Date constructor silently rolls them over
      // (to Mar 3), so verify the round-trip before formatting and otherwise show the raw value.
      const isValid =
        !Number.isNaN(date.getTime()) &&
        date.getFullYear() === y &&
        date.getMonth() === m - 1 &&
        date.getDate() === d;
      if (isValid) {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      }
    }
    return value;
  }

  return value;
};

export const AuditLog: React.FC<AuditLogProps> = ({ caseId }) => {
  const { authClient } = React.useContext(Context as ReactContext<IEmployeeExperienceContext>);
  const [audits, setAudits] = React.useState<IPBTCaseAudit[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const loadAuditTrail = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = await authClient.acquireToken(__SCOPES__);
        if (!token) {
          throw new Error('Failed to acquire authentication token');
        }
        const auditData = await getCaseAuditApi(token, caseId);
        setAudits(auditData);
      } catch (ex) {
        setError((ex as Error).message || 'Failed to load audit trail');
      } finally {
        setIsLoading(false);
      }
    };

    loadAuditTrail();
  }, [caseId, authClient]);

  if (isLoading) {
    return (
      <div className={auditLogStyles.loadingContainer}>
        <Spinner size={SpinnerSize.medium} label="Loading audit trail..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={auditLogStyles.errorContainer}>
        <Icon iconName="ErrorBadge" style={{ fontSize: 24, color: '#a4262c', marginBottom: 8 }} />
        <Text>{error}</Text>
      </div>
    );
  }

  if (audits.length === 0) {
    return (
      <div className={auditLogStyles.emptyContainer}>
        <Icon iconName="History" style={{ fontSize: 48, color: '#c8c6c4', marginBottom: 16 }} />
        <Text style={{ color: '#605e5c' }}>No changes have been logged for this case yet.</Text>
      </div>
    );
  }

  return (
    <div className={auditLogStyles.container}>
      {audits.map((audit, idx) => (
        <div key={`${audit.auditId}-${idx}`} className={auditLogStyles.entry}>
          <div className={auditLogStyles.timeline}>
            <div className={auditLogStyles.dot} />
            {idx < audits.length - 1 && <div className={auditLogStyles.line} />}
          </div>
          <div className={auditLogStyles.content}>
            <div className={auditLogStyles.header}>
              <Text className={auditLogStyles.user}>{audit.changedBy}</Text>
              <Text className={auditLogStyles.timestamp}>{formatDateTime(audit.changedAt)}</Text>
            </div>
            <div className={auditLogStyles.change}>
              <Text className={auditLogStyles.fieldName}>{audit.fieldName}</Text>
              <div className={auditLogStyles.values}>
                <span className={auditLogStyles.oldValue}>{formatAuditValue(audit.fieldName, audit.oldValue)}</span>
                <Icon iconName="Forward" style={{ margin: '0 8px', fontSize: 12, color: '#605e5c' }} />
                <span className={auditLogStyles.newValue}>{formatAuditValue(audit.fieldName, audit.newValue)}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
